import React, { useState } from 'react';
import EpdVerificationDescription from 'components/v2/copy/epd-details/EpdVerificationDescription';
import ProcessCertificateDescription from 'components/v2/copy/epd-details/ProcessCertificateDescription';
import EpdPanelHeaderProcessCertificateTemplate from 'components/v2/copy/header/EpdPanelHeaderProcessCertificateTemplate';
import EpdPanelHeaderVerificationStepTemplate from 'components/v2/copy/header/EpdPanelHeaderVerificationStepTemplate';
import { HelpBox } from 'components/v2/help-boxes';
import { Paginator } from 'primereact/paginator';
import { EPDModel, EpdCopyGroupWizardModel, FileLoadingModel, VerificationType, VersionStateType } from 'services/EpdClient';
import { EpdItemPanel, FieldPanelFullWidth } from 'styles/v2/Styles.styled';

const WizardStep3: React.FunctionComponent<{
  groupData?: EpdCopyGroupWizardModel;
  onChangeEpd(id: string, name: string, value: any): Promise<void>;
  onUploadFile(
    epdId: string | undefined,
    field: string,
    file: File,
    documentTypeId: string | null,
    documentLanguageId: string | null
  ): void;
  onRemoveFile(versionId: string, fileId: string): Promise<void>;
  refreshEPD: any;
  canUpload: boolean;
  isReadOnly: boolean;
  updateGroupCertificateFilesArray: any;
  fileLoading?: FileLoadingModel[];
}> = ({
  groupData,
  onChangeEpd,
  onUploadFile,
  onRemoveFile,
  refreshEPD,
  canUpload,
  isReadOnly,
  updateGroupCertificateFilesArray,
  fileLoading,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const handleChangePage = (event: any) => {
    setCurrentPage(event.page + 1); // Paginator starts counting from 0
  };

  if (groupData?.verificationType === VerificationType.EPDPreverifiedTool) {
    return (
      <FieldPanelFullWidth>
        <HelpBox>This verification flow is based on the pre-verified tool, no actions needed</HelpBox>
      </FieldPanelFullWidth>
    );
  }

  const isEveryEpdLockedBecauseItCannotBeEditedAfterDeclinedPublication =
    groupData?.epds?.every((epd) => epd.versionState === VersionStateType.DeclinedPublication) ?? false;

  return (
    <div>
      {groupData?.verificationType === VerificationType.EPDProcessCertification && (
        <EpdItemPanel
          key={'ProcessCertificatesFiles'}
          headerTemplate={(options) =>
            EpdPanelHeaderProcessCertificateTemplate(
              options,
              groupData?.processCertificatesFiles || [],
              groupData?.id || '',
              updateGroupCertificateFilesArray,
              canUpload && !isEveryEpdLockedBecauseItCannotBeEditedAfterDeclinedPublication
            )
          }
          toggleable
          collapsed
        >
          <ProcessCertificateDescription processCertificates={groupData?.processCertificatesFiles || []} />
        </EpdItemPanel>
      )}

      {groupData?.epds?.slice(startIdx, startIdx + ITEMS_PER_PAGE).map((item: EPDModel) => {
        const isEpdLockedBecauseItCannotBeEditedAfterDeclinedPublication =
          item.versionState === VersionStateType.DeclinedPublication;
        return (
          <EpdItemPanel
            key={item.id}
            headerTemplate={(options) =>
              EpdPanelHeaderVerificationStepTemplate(
                options,
                item,
                (name: string, value: any) => onChangeEpd(item.id!, name, value),
                (field, file) => onUploadFile(item.id, field, file, null, null),
                (fileId) => onRemoveFile(item.versionId!, fileId),
                refreshEPD,
                canUpload && !isEpdLockedBecauseItCannotBeEditedAfterDeclinedPublication,
                isReadOnly,
                fileLoading
              )
            }
            toggleable
            collapsed
          >
            <EpdVerificationDescription
              epd={item}
              groupStatus={groupData?.status}
              isVerifier={groupData?.userRoles?.isVerifier}
              onRemoveFile={(fileId) => onRemoveFile(item.versionId!, fileId)}
              isReadOnly={isReadOnly || isEpdLockedBecauseItCannotBeEditedAfterDeclinedPublication}
            />
          </EpdItemPanel>
        );
      })}

      <Paginator
        className="paginator-right"
        style={{ padding: 0, marginTop: '1rem' }}
        alwaysShow={false}
        first={startIdx}
        rows={ITEMS_PER_PAGE}
        totalRecords={groupData?.epds?.length}
        onPageChange={handleChangePage}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        rowsPerPageOptions={[ITEMS_PER_PAGE]}
      />
    </div>
  );
};

export default WizardStep3;
